import { Collapse, styled } from '@mui/material';

import { CollapseIcon, ExpandIcon } from '../../../icons';
import { Card } from '../Card';
import {
  type ComparisonMatrixCriterion,
  type ComparisonMatrixItem,
  type ComparisonMatrixProps,
  useComparisonMatrix,
} from './useComparisonMatrix';

type Props = ComparisonMatrixProps;

const CRITERIA_COLUMN_WIDTH = '12rem';
const ITEM_COLUMN_MIN_WIDTH = '14rem';

type CellOwnerState = {
  numeric: boolean;
};

/* Matrix (desktop) */

const MatrixScroller = styled('div')(({ theme }) => ({
  overflowX: 'auto',
  overscrollBehaviorX: 'contain',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

const MatrixTable = styled('table')({
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
});

const CornerCell = styled('th')(({ theme }) => ({
  ...theme.typography.body2Emphasis,
  position: 'sticky',
  left: 0,
  zIndex: 2,
  width: CRITERIA_COLUMN_WIDTH,
  minWidth: CRITERIA_COLUMN_WIDTH,
  padding: theme.spacing(2),
  textAlign: 'left',
  verticalAlign: 'bottom',
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.elevation1,
  borderRight: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ItemHeaderCell = styled('th')(({ theme }) => ({
  minWidth: ITEM_COLUMN_MIN_WIDTH,
  padding: theme.spacing(2),
  textAlign: 'left',
  verticalAlign: 'top',
  fontWeight: 'inherit',
  backgroundColor: theme.palette.background.elevation1,
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& + &': {
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
}));

const ItemHeading = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
}));

const ItemHeadingText = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  flex: 1,
  minWidth: 0,
}));

const ItemTitle = styled('span')(({ theme }) => ({
  ...theme.typography.subtitle1,
  color: theme.palette.text.primary,
  overflowWrap: 'anywhere',
}));

const ItemMeta = styled('div')(({ theme }) => ({
  ...theme.typography.body2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  fontVariantNumeric: 'tabular-nums',
}));

const ItemAction = styled('div')(({ theme }) => ({
  flexShrink: 0,
  marginTop: theme.spacing(-0.5),
  marginRight: theme.spacing(-1),
}));

const CriterionCell = styled('th')(({ theme }) => ({
  ...theme.typography.body2Emphasis,
  position: 'sticky',
  left: 0,
  zIndex: 1,
  padding: theme.spacing(1.5, 2),
  textAlign: 'left',
  verticalAlign: 'top',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
  overflowWrap: 'anywhere',
  'tr + tr > &': {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const ValueCell = styled('td')<{ ownerState: CellOwnerState }>(({ theme, ownerState }) => ({
  ...(ownerState.numeric ? theme.typography.body1Emphasis : theme.typography.body1),
  padding: theme.spacing(1.5, 2),
  textAlign: ownerState.numeric ? 'right' : 'left',
  verticalAlign: 'top',
  color: theme.palette.text.primary,
  fontVariantNumeric: 'tabular-nums',
  overflowWrap: 'anywhere',
  'tr + tr > &': {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  '& + &': {
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
}));

/* Card deck (phones and tablets) */

const Deck = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  alignItems: 'start',
  gap: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
}));

const DeckHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  paddingRight: theme.spacing(1),
}));

const ToggleButton = styled('button')(({ theme }) => ({
  ...theme.typography.body1,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flex: 1,
  minWidth: 0,
  minHeight: theme.spacing(7),
  margin: 0,
  padding: theme.spacing(1.5, 2),
  paddingRight: theme.spacing(1),
  border: 0,
  background: 'none',
  color: theme.palette.text.primary,
  textAlign: 'left',
  cursor: 'pointer',
  borderRadius: theme.shape.borderRadius,
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
}));

const ToggleText = styled('span')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  columnGap: theme.spacing(1),
  rowGap: theme.spacing(0.5),
  flex: 1,
  minWidth: 0,
}));

const DeckTitle = styled('span')(({ theme }) => ({
  ...theme.typography.subtitle1,
  overflowWrap: 'anywhere',
}));

const DeckSummary = styled('span')(({ theme }) => ({
  ...theme.typography.body1Emphasis,
  marginLeft: 'auto',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
}));

const ToggleIcon = styled('span')(({ theme }) => ({
  display: 'flex',
  flexShrink: 0,
  color: theme.palette.text.secondary,
}));

const DeckAction = styled('div')(({ theme }) => ({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  minHeight: theme.spacing(7),
}));

const DeckMeta = styled(ItemMeta)(({ theme }) => ({
  paddingInline: theme.spacing(2),
  paddingBottom: theme.spacing(1.5),
}));

const Facts = styled('dl')(({ theme }) => ({
  margin: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const Fact = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  padding: theme.spacing(1.5, 2),
  '& + &': {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const FactLabel = styled('dt')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
}));

const FactValue = styled('dd')<{ ownerState: CellOwnerState }>(({ theme, ownerState }) => ({
  ...(ownerState.numeric ? theme.typography.body1Emphasis : theme.typography.body1),
  margin: 0,
  color: theme.palette.text.primary,
  fontVariantNumeric: 'tabular-nums',
  overflowWrap: 'anywhere',
}));

function getNumericState(criterion: ComparisonMatrixCriterion): CellOwnerState {
  return { numeric: Boolean(criterion.numeric) };
}

/**
 * Side-by-side comparison of a few items (quotes, offers) against shared criteria.
 * From `md` it is a matrix: one column per item, one row per criterion, the criteria column
 * sticky while the matrix scrolls inside its own container. Below `md` it becomes a deck of
 * collapsible cards (one column on phones, two on tablets), each listing the criteria of its item;
 * `summaryCriterionId` keeps one key value (the price) visible on a collapsed card.
 */
export function ComparisonMatrix(props: Props) {
  const hook = useComparisonMatrix(props);

  if (hook.isDeck) {
    return (
      <Deck aria-label={props['aria-label']} data-testid={props['data-testid'] ?? 'comparison-matrix'}>
        {props.items.map((item) => renderDeckItem(item))}
      </Deck>
    );
  }

  return (
    <MatrixScroller
      role="region"
      aria-label={props['aria-label']}
      tabIndex={0}
      data-testid={props['data-testid'] ?? 'comparison-matrix'}
    >
      <MatrixTable>
        <thead>
          <tr>
            <CornerCell scope="col">{hook.criteriaLabel}</CornerCell>
            {props.items.map((item) => (
              <ItemHeaderCell key={item.id} scope="col">
                <ItemHeading>
                  <ItemHeadingText>
                    <ItemTitle>{item.title}</ItemTitle>
                    {item.meta ? <ItemMeta>{item.meta}</ItemMeta> : null}
                  </ItemHeadingText>
                  {item.action ? <ItemAction>{item.action}</ItemAction> : null}
                </ItemHeading>
              </ItemHeaderCell>
            ))}
          </tr>
        </thead>
        <tbody>
          {props.criteria.map((criterion) => (
            <tr key={criterion.id}>
              <CriterionCell scope="row">{criterion.label}</CriterionCell>
              {props.items.map((item) => (
                <ValueCell key={item.id} ownerState={getNumericState(criterion)}>
                  {hook.renderValue(criterion.values[item.id])}
                </ValueCell>
              ))}
            </tr>
          ))}
        </tbody>
      </MatrixTable>
    </MatrixScroller>
  );

  function renderDeckItem(item: ComparisonMatrixItem) {
    const expanded = hook.isExpanded(item.id);
    const panelId = hook.getPanelId(item.id);

    return (
      <li key={item.id}>
        <Card>
          <DeckHeader>
            <ToggleButton
              type="button"
              aria-expanded={expanded}
              aria-controls={panelId}
              onClick={() => hook.toggleItem(item.id)}
            >
              <ToggleText>
                <DeckTitle>{item.title}</DeckTitle>
                {hook.summaryCriterion ? (
                  <DeckSummary>{hook.renderValue(hook.summaryCriterion.values[item.id])}</DeckSummary>
                ) : null}
              </ToggleText>
              <ToggleIcon aria-hidden="true">
                {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
              </ToggleIcon>
            </ToggleButton>
            {item.action ? <DeckAction>{item.action}</DeckAction> : null}
          </DeckHeader>

          {item.meta ? <DeckMeta>{item.meta}</DeckMeta> : null}

          <Collapse in={expanded} id={panelId}>
            <Facts>
              {props.criteria.map((criterion) => (
                <Fact key={criterion.id}>
                  <FactLabel>{criterion.label}</FactLabel>
                  <FactValue ownerState={getNumericState(criterion)}>
                    {hook.renderValue(criterion.values[item.id])}
                  </FactValue>
                </Fact>
              ))}
            </Facts>
          </Collapse>
        </Card>
      </li>
    );
  }
}

